import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { useFormat } from '@brightdrop/localization-core';

import { DEFAULT_DATE_FORMAT } from '~/common/constants/common.constant';
import { LOCALIZATION_FEATURE_FLAG } from '~/common/models/featureFlags.model';

/**
 * The pattern a calendar day is written in on screen.
 *
 * A pattern rather than a formatted string, because the two surfaces that need this need it in
 * different forms: a picker has to be told the shape it displays, while the chip beside it writes
 * a day with the same shape. Handing both the pattern is what keeps them from disagreeing about
 * which day is filtered.
 *
 * The flag is read here rather than left to `datePattern()`, which answers `en-US` on its own
 * while the core is switched off. That covers one of the two switches. The core being on does not
 * mean a feature has been migrated, and in that middle state `datePattern()` would follow the
 * reader on a screen that is meant to be unchanged — so the narrower flag has to be asked
 * explicitly. Off, this is the pattern the pickers hard-coded before any of this existed.
 *
 * @returns The reader's date pattern once both switches are on, and `MM/dd/yyyy` otherwise.
 */
export const useDayPattern = (): string => {
  const { getFlag } = useFeatureFlags();
  const { datePattern } = useFormat();

  return getFlag(LOCALIZATION_FEATURE_FLAG)
    ? datePattern()
    : DEFAULT_DATE_FORMAT;
};
